"use client";

import { builder } from "@/builders";
import { Emblem } from "@idss/interface";
import { APP, makePath, PAGES, TOKEN } from "@idss/libraries";
import { AppShell, ScrollArea, Stack } from "@mantine/core";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { boolean } from "mathjs";
import { useCookies } from "next-client-cookies";

import { navigate } from "@/actions/navigate";
import { AppShellToggleButton } from "@/components/admin/shared/app-shell-toggle-button";
import { ProfilePopover } from "@/components/staff/dashboard/profile-popover";
import { AppShellButton } from "@/components/staff/shared/app-shell-button";
import { useNavbar } from "@/hooks/use-navbar";
import { DashboardIcon } from "@/svgs/dashboard-icon";
import { DepartmentIcon } from "@/svgs/department-icon";
import { MyCoursesIcon } from "@/svgs/my-courses-icon";
import { QuestionBanksIcon } from "@/svgs/question-banks-icon";
import { TechnicalSessionIcon } from "@/svgs/technical-session-icon";

type TemplateProps = React.PropsWithChildren;

export default function Template({ children }: TemplateProps) {
  const cookies = useCookies();

  const collapsedNav = cookies.get(APP.EXPANDED_NAVBAR);
  const opened = boolean(collapsedNav ?? true);
  const uniqueLink = cookies.get(APP.UNIQUE_LINK);

  if (!uniqueLink) navigate(PAGES.DASHBOARD);
  if (!cookies.get(TOKEN.HEADER)) {
    navigate(makePath(PAGES.SIGNIN));
  }

  const { data, isFetching } = useSuspenseQuery({
    queryKey: builder.unique_links.get.$get(uniqueLink),
    queryFn: () => builder.$use.unique_links.get(uniqueLink!),
    select: ({ data }) => data,
  });

  const { data: profile } = useQuery({
    queryKey: builder.account.profile.get.$get(),
    queryFn: () => builder.$use.account.profile.get(),
    select: ({ data }) => data,
  });

  const { opened: navbarOpened } = useNavbar();

  return (
    <AppShell
      bg="accent.12"
      navbar={{
        width: opened ? 280 : 80,
        breakpoint: "sm",
        collapsed: { mobile: !navbarOpened },
      }}
      styles={{
        navbar: {
          backgroundColor: "var(--accent-12)",
          zIndex: "999 !important",
        },
      }}
    >
      <AppShell.Navbar
        h="100%"
        px={opened ? 24 : 12}
        py={32}
        withBorder={false}
        style={{
          alignItems: opened ? "unset" : "center",
        }}
      >
        <AppShell.Section className="mb-4 ml-auto" hiddenFrom="sm">
          <AppShellToggleButton />
        </AppShell.Section>

        <AppShell.Section>
          <Emblem
            image={data?.logo}
            name={data?.name}
            skeleton={isFetching}
            justify="center"
            c="gray.1"
          />
        </AppShell.Section>

        <AppShell.Section grow pt={60} component={ScrollArea}>
          <Stack gap={8}>
            <AppShellButton
              leftSection={<DashboardIcon />}
              href={PAGES.DASHBOARD}
              label="Dashboard"
              opened={opened}
            />
            <AppShellButton
              leftSection={<DepartmentIcon />}
              href={PAGES.LEARNING_GROUPS}
              label="Learning Specialty"
              opened={opened}
            />
            <AppShellButton
              leftSection={<MyCoursesIcon />}
              href={PAGES.COURSES}
              label="My Courses"
              opened={opened}
            />
            <AppShellButton
              leftSection={<QuestionBanksIcon />}
              href={PAGES.QUESTION_BANK}
              label="Question Bank"
              opened={opened}
            />
            <AppShellButton
              leftSection={<TechnicalSessionIcon />}
              href={PAGES.TECHNICAL_SESSION}
              label="Technical Session"
              opened={opened}
            />
          </Stack>
        </AppShell.Section>

        <AppShell.Section component={Stack}>
          <ProfilePopover {...profile} />

          {/* <Divider />

          <SegmentedControl
            color="accent.9"
            data={["light", "dark"]}
            value={colorScheme}
            onChange={(value) => {
              setColorScheme(value as ColorScheme);
            }}
          /> */}
        </AppShell.Section>
      </AppShell.Navbar>

      <AppShell.Main bg="accent.12" className="overflow-auto">
        <Stack gap={0} ml={0} mb={0} flex={1}>
          {children}
        </Stack>
      </AppShell.Main>
    </AppShell>
  );
}
